/**
 * Danish address search and zip-to-city, for plain (non-entity) forms.
 *
 * Everything goes through the dk_address module's endpoints, which talk to
 * Klimadatastyrelsen's Adressevælgeren for us:
 *
 * - /dk-address/address/select2?q=… returns suggestions. An id that begins
 *   with "refine:" is a narrower search rather than an address - a street
 *   shared by many towns, or a house number with floors and doors behind it.
 * - /dk-address/address/resolve?id=… turns a picked id into the address in
 *   parts, which we spread over the fields marked with data-address-fields.
 * - /dk-address/postal-code?nr=… gives the town for a postal code.
 *
 * See the "Using the lookup without the field" and "Looking up a postal code"
 * sections of dk_address' README.
 */
Drupal.behaviors.toybox_address = {
  attach: function (context) {
    once("address-search", ".js-address-search", context).forEach((input) =>
      this.initSearchField(input),
    );
    once("address-postal-code", ".js-address-postal-code", context).forEach(
      (input) => this.initPostalCodeField(input),
    );
  },

  /**
   * Wraps a function so it only runs once the user pauses typing.
   *
   * This avoids firing a request on every keystroke.
   *
   * @param {Function} callback - The function to debounce.
   * @param {number} delay - The idle time in milliseconds to wait before running.
   * @return {Function} The debounced function.
   */
  debounce: function (callback, delay = 100) {
    let timeout;

    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(function () {
        callback.apply(this, args);
      }, delay);
    };
  },

  /**
   * Fetches JSON, returning null on any failure.
   *
   * Callers treat null as "nothing to offer", so the form degrades to typing
   * the address by hand instead of throwing. A 404 is an ordinary answer -
   * a postal code dk_address doesn't know - so only other failures are logged.
   *
   * @param {string} url - The URL to fetch.
   * @return {Promise<*>} The decoded response, or null.
   */
  fetchJson: async function (url) {
    try {
      const response = await fetch(url);

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        console.error(
          `Address request failed: ${response.status} ${response.statusText} (${url})`,
        );
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error(`Address request errored (${url})`, error);
      return null;
    }
  },

  /**
   * Sets up an address search input with a list of suggestions below it.
   *
   * @param {HTMLInputElement} input - The address search input.
   */
  initSearchField: function (input) {
    const suggestionsWrapper = document.createElement("div");
    suggestionsWrapper.classList.add("control__suggestions-items");
    input.parentNode.insertBefore(suggestionsWrapper, input.nextSibling);

    const choose = (target) => {
      if (target.dataset.addressId) {
        this.chooseSuggestion(target.dataset.addressId, input);
      }
    };

    suggestionsWrapper.addEventListener("click", (event) =>
      choose(event.target),
    );
    // The suggestions are role="button", so they have to act like one.
    suggestionsWrapper.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        choose(event.target);
      }
    });

    input.addEventListener(
      "input",
      this.debounce(() => this.updateSuggestions(input)),
    );
  },

  /**
   * Fetches and displays suggestions for what is in the search input.
   *
   * @param {HTMLInputElement} input - The address search input.
   */
  updateSuggestions: async function (input) {
    const suggestionsWrapper = input.nextElementSibling;
    const query = input.value.trim();

    // Responses can arrive out of order. Only the latest search may render.
    const request = (input.addressRequest = (input.addressRequest || 0) + 1);

    if (!query) {
      suggestionsWrapper.replaceChildren();
      return;
    }

    const data = await this.fetchJson(
      `${Drupal.url("dk-address/address/select2")}?q=${encodeURIComponent(query)}`,
    );

    if (request !== input.addressRequest) {
      return;
    }

    suggestionsWrapper.replaceChildren(
      ...(data?.results || []).map((result) => {
        const div = document.createElement("div");
        div.tabIndex = 0;
        div.setAttribute("role", "button");
        div.dataset.addressId = result.id;
        div.textContent = result.text;
        return div;
      }),
    );
  },

  /**
   * Acts on a picked suggestion: narrows the search, or fills in the address.
   *
   * @param {string} id - The suggestion's id, as the search returned it.
   * @param {HTMLInputElement} input - The address search input.
   */
  chooseSuggestion: async function (id, input) {
    const refinePrefix = "refine:";

    if (id.startsWith(refinePrefix)) {
      input.value = id.slice(refinePrefix.length);
      input.focus();
      this.updateSuggestions(input);
      return;
    }

    input.nextElementSibling.replaceChildren();

    const address = await this.fetchJson(
      `${Drupal.url("dk-address/address/resolve")}?id=${encodeURIComponent(id)}`,
    );

    if (address) {
      this.fillAddressFields(address, input);
    }
  },

  /**
   * Fills the search input, and the form's address fields, from an address.
   *
   * Each field names the keys it wants in data-address-fields, joined with
   * "+". The values are joined with a comma, skipping any the address does not
   * have - so "address+supplerendebynavn" gives "Svogerslev Hovedgade 4A,
   * Svogerslev", or just "Suomisvej 2".
   *
   * @param {Object} address - The address, as the resolve endpoint returns it.
   * @param {HTMLInputElement} input - The address search input.
   */
  fillAddressFields: function (address, input) {
    input.value = address.value;

    const scope = input.form || document;

    scope.querySelectorAll("[data-address-fields]").forEach((field) => {
      field.value = field.dataset.addressFields
        .split("+")
        .map((key) => address[key])
        .filter(Boolean)
        .join(", ");
      field.dispatchEvent(new Event("change"));
    });
  },

  /**
   * Sets up a zip input, filling in the city fields of its form.
   *
   * @param {HTMLInputElement} input - The zip input.
   */
  initPostalCodeField: function (input) {
    input.addEventListener(
      "input",
      this.debounce(() => this.setCityFromPostalCode(input)),
    );
  },

  /**
   * Looks up the city for a zip, and fills it in.
   *
   * A zip dk_address doesn't know may just be newer than its list, so the
   * city is left for the visitor to type rather than cleared.
   *
   * @param {HTMLInputElement} input - The zip input.
   */
  setCityFromPostalCode: async function (input) {
    const postalCode = input.value.trim();
    const cityInputs = (input.form || document).querySelectorAll(
      '[data-address-fields="postal_name"]',
    );

    // Only a full Danish zip can be looked up.
    if (!/^\d{4}$/.test(postalCode) || !cityInputs.length) {
      return;
    }

    const data = await this.fetchJson(
      `${Drupal.url("dk-address/postal-code")}?nr=${postalCode}`,
    );

    if (!data?.postal_name) {
      return;
    }

    cityInputs.forEach((cityInput) => {
      cityInput.value = data.postal_name;
    });
  },
};
